import { BrowserWindowEvent } from 'pf-frontend-common/src/module/browser/window.event';
import { EventEmitterInterface } from 'pf-frontend-common/src/module/event/emitter.interface';
import { WindowServiceInterface } from 'pf-frontend-common/src/service/window/service.interface';

import { BackendConfigEnvInterface } from 'common/data/backend/config/env.interface';
import { BackendConfigEnvPlatformEnum } from 'common/data/backend/config/env-platform.enum';
import { arrayToggle } from 'common/helper/array/toggle';
import { DataStore } from 'common/module/data/store';
import { DropdownTemplatePropsInterface } from 'common/module/dropdown/template-props.interface';
import { DropdownViewStoreEvent } from 'common/module/dropdown/view-store.event';
import { DropdownViewStoreOptionsInterface } from 'common/module/dropdown/view-store-options.interface';
import { InputSelectOptionInterface } from 'common/module/input/select/option.interface';
import { KeyboardCharcodeEnum } from 'common/module/keyboard/charcode.enum';

export class DropdownViewStore extends DataStore {
  /**
   * @inheritDoc
   */
  protected state: DropdownTemplatePropsInterface;

  /**
   * Saved value to restore if the dropdown selection is cancelled
   */
  private cachedValue: string | string[];

  /**
   * Constructor
   */
  constructor(
    eventEmitter: EventEmitterInterface,
    private windowService: WindowServiceInterface,
    private envConfig: BackendConfigEnvInterface
  ) {
    super(eventEmitter);
  }

  /**
   * @inheritDoc
   */
  public getState(): DropdownTemplatePropsInterface {
    return this.state;
  }

  /**
   * @inheritDoc
   */
  public initialize(options?: DropdownViewStoreOptionsInterface): void {
    this.destroy();
    this.state = {
      class: (options && options.class) || '',
      valueClass: (options && options.valueClass) || '',
      iconClass: (options && options.iconClass) || '',
      iconOpenClass: (options && options.iconOpenClass) || '',
      optionsClass: (options && options.optionsClass) || '',
      multiple: (options && options.multiple) || false,
      placeholder: options && options.placeholder,
      options: options && options.options,
      value: options && options.value,
      isOpen: !!(options && options.isOpen),
      highlighted: null,
      onClickDropdown: this.onClickDropdown,
      onClickSelectAll: this.onClickSelectAll,
      onMouseEnterOption: this.onMouseEnterOption,
      onMouseLeaveOption: this.onMouseLeaveOption,
      onClickOption: this.onClickOption,
    };

    if (options) {
      this.cacheValue(options.value);
    }

    this.windowService.getEventEmitter().addListener(BrowserWindowEvent.keydown, this.onKeydownWindow);
    this.windowService.getEventEmitter().addListener(BrowserWindowEvent.click, this.onClickWindow);
  }

  /**
   * Set the value of the dropdown
   */
  public setValue(value: string | string[]): void {
    const state = this.getState();
    state.value = value;
    this.setState(state);
  }

  /**
   * Close the view and emit a change event if changed
   */
  public close(): void {
    const state = this.getState();

    if (!state.isOpen) {
      return;
    }

    if (state.value !== this.cachedValue) {
      this.cacheValue(state.value);
      this.getEventEmitter().emit(DropdownViewStoreEvent.change, state.value);
    }

    this.updateHighlighted(null);
    this.updateOpen(false);
    this.getEventEmitter().emit(DropdownViewStoreEvent.close, state.value);
  }

  /**
   * Remove any existing listeners
   */
  public destroy(): void {
    this.windowService.getEventEmitter().removeListener(BrowserWindowEvent.keydown, this.onKeydownWindow);
  }

  /**
   * Save the current value
   */
  private cacheValue(value: string | string[]): void {
    this.cachedValue = value;
  }

  /**
   * Restore the previously saved value
   */
  private restoreCachedValue(): void {
    const state = this.getState();
    state.value = this.cachedValue;
    this.setState(state);
  }

  /**
   * Set the visibility of the dropdown
   */
  private updateOpen(isOpen: boolean): void {
    const state = this.getState();

    if (state.isOpen === isOpen) {
      return;
    }

    state.isOpen = isOpen;
    this.setState(state);
  }

  /**
   * Update the currently highlighted value
   */
  private updateHighlighted(value: string): void {
    const state = this.getState();
    state.highlighted = value;
    this.setState(state);
  }

  /**
   * Update selected value
   */
  private updateValue(value: string): void {
    const state = this.getState();

    if (state.multiple) {
      state.value = arrayToggle(value, <string[]>state.value || []);
    } else {
      state.value = value;
    }

    this.setState(state);
  }

  /**
   * Clicked dropdown
   */
  private onClickDropdown = (e: Event) => {
    e.preventDefault();

    if (!this.getState().isOpen) {
      this.updateOpen(true);
      return;
    }

    this.close();
  };

  /**
   * Clicked an option
   */
  private onClickOption = (e: Event, option: InputSelectOptionInterface) => {
    e.preventDefault();
    e.stopPropagation();

    const state = this.getState();

    this.updateHighlighted(option.value);
    this.updateValue(state.highlighted);

    if (!state.multiple) {
      this.close();
    }
  };

  /**
   * Clicked select all
   */
  private onClickSelectAll = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();

    const state = this.getState();

    if (state.value.length === state.options.length) {
      state.value = [];
    } else {
      state.value = state.options.map((option) => option.value);
    }

    this.setState(state);
  };

  /**
   * Mouse entered an option
   */
  private onMouseEnterOption = (e: Event, option: InputSelectOptionInterface) => {
    if (this.envConfig.platform === BackendConfigEnvPlatformEnum.desktop) {
      this.updateHighlighted(option.value);
    }
  };

  /**
   * Mouse left an option
   */
  private onMouseLeaveOption = () => {
    if (this.envConfig.platform === BackendConfigEnvPlatformEnum.desktop) {
      this.updateHighlighted(null);
    }
  };

  /**
   * Clicked the window
   */
  private onClickWindow = (e: Event) => {
    this.getEventEmitter().emit(DropdownViewStoreEvent.clickWindow, e);
  };

  /**
   * Key was pressed
   */
  private onKeydownWindow = (e: KeyboardEvent) => {
    const state = this.getState();
    const curIndex = state.options.map((option) => option.value).indexOf(state.highlighted);

    if (!state.isOpen) {
      return;
    }

    switch (e.keyCode) {
      case KeyboardCharcodeEnum.down:
        e.preventDefault();
        this.updateHighlighted(state.options[Math.min(curIndex + 1, state.options.length - 1)].value);
        break;

      case KeyboardCharcodeEnum.up:
        e.preventDefault();
        this.updateHighlighted(state.options[Math.max(curIndex - 1, 0)].value);
        break;

      case KeyboardCharcodeEnum.escape:
        this.restoreCachedValue();
        this.close();
        break;

      case KeyboardCharcodeEnum.enter:
        if (state.highlighted !== undefined) {
          this.updateValue(state.highlighted);
        }
        if (!state.multiple) {
          this.close();
        }
        break;

      default:
        break;
    }
  };
}
